import { BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/breadcrumb";
import { Link, VStack } from "@chakra-ui/layout";
import { HStack, Box, useDisclosure } from "@chakra-ui/react";
import React, { useState } from "react";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import { BusinessMenu } from "../../components/Business/BusinessMenu";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { useBusinessContext } from "../../context/ModelContext";
import { useSearchInput } from "../../hooks/useSearchInput";
import { PlusIcon } from "../../styles/icons";
import { usePaginatedAdminApiRequest } from "../../hooks/usePaginatedApiRequest";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { useNavigate } from "react-router-dom";
import { PassConfigurationTable } from "../../components/PassConfiguration/PassConfigurationTable";
import { CopyPassConfigurationDrawer } from "../../components/PassConfiguration/CopyPassConfigurationDrawer";
import { ClonePassConfigurationDrawer } from "../../components/PassConfiguration/ClonePassConfigurationDrawer";
import { PassConfigurationUpdateHistoryDrawer } from "../../components/PassConfiguration/PassConfigurationUpdateHistoryDrawer";
import { NewDigitalMembershipCardPassConfigModal } from "../../components/Business/NewDigitalMembershipCardPassConfigModal";

export const PassConfigurations = () => {
  const business = useBusinessContext();
  const navigate = useNavigate();
  const { debouncedQuery, element: searchInputElement } = useSearchInput({
    placeholder: "Search pass configurations...",
  });
  const [selectedPassConfiguration, setSelectedPassConfiguration] =
    useState<CherryHubAdminApi.CherryPassConfigurationSummaryItem | null>(null);

  const onClickEditPassBuilder = (
    passConfiguration: CherryHubAdminApi.CherryPassConfigurationSummaryItem
  ) =>
    navigate(
      `/admin/businesses/${business.BusinessId}/pass-configurations/${passConfiguration.Meta?.Id}`
    );

  const {
    items: passConfigurationList,
    isLoading,
    refresh,
  } = usePaginatedAdminApiRequest(
    // Request the first page
    (apiClient) =>
      apiClient.getBusinessDigitalMembershipCardPassConfiguration(
        business.BusinessId as string,
        debouncedQuery
      ),
    // Request subsequent pages
    (apiClient, _, continuationToken) =>
      apiClient.getBusinessDigitalMembershipCardPassConfiguration(
        business.BusinessId as string,
        debouncedQuery,
        undefined,
        continuationToken
      ),
    [business.BusinessId, debouncedQuery]
  );

  const newPassConfigModal = useDisclosure();
  const copyPassConfigurationDrawer = useDisclosure();
  const clonePassConfigurationDrawer = useDisclosure();
  const updateHistoryDrawer = useDisclosure();

  const onClickCopyPassConfiguration = (
    passConfiguration: CherryHubAdminApi.CherryPassConfigurationSummaryItem
  ) => {
    setSelectedPassConfiguration(passConfiguration);
    copyPassConfigurationDrawer.onOpen();
  };

  const onCloseCopyPassConfigurationDrawer = () => {
    copyPassConfigurationDrawer.onClose();
    setSelectedPassConfiguration(null);
  };

  const onClickClonePassConfiguration = (
    passConfiguration: CherryHubAdminApi.CherryPassConfigurationSummaryItem
  ) => {
    setSelectedPassConfiguration(passConfiguration);
    clonePassConfigurationDrawer.onOpen();
  };

  const onCloseClonePassConfigurationDrawer = () => {
    clonePassConfigurationDrawer.onClose();
    setSelectedPassConfiguration(null);
  };

  const onClickViewUpdateHistory = (
    passConfiguration: CherryHubAdminApi.CherryPassConfigurationSummaryItem
  ) => {
    setSelectedPassConfiguration(passConfiguration);
    updateHistoryDrawer.onOpen();
  };

  const onCloseUpdateHistoryDrawer = () => {
    updateHistoryDrawer.onClose();
    setSelectedPassConfiguration(null);
  };

  const onClonedPassConfiguration = (
    passConfiguration: CherryHubAdminApi.CherryPassConfigurationSummaryItem
  ) => {
    if (!passConfiguration.Meta?.Id) {
      refresh();
      return;
    }

    onClickEditPassBuilder(passConfiguration);
  };

  return (
    <>
      <PageHeading>
        <PageHeading.Title>Pass Configurations</PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack alignItems="flex-start" width="100%">
          <BusinessBreadcrumbs variant="pageHeading">
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${business.BusinessId}/pass-configurations`}
              >
                Pass Configurations
              </BreadcrumbLink>
            </BreadcrumbItem>
          </BusinessBreadcrumbs>

          <HStack w="100%" justifyContent="space-between">
            <Box flex="4" maxWidth="400px">
              {searchInputElement}
            </Box>
            <Box marginLeft="auto">
              <BusinessMenu
                businessId={business.Meta?.Id!}
                leftIcon={<PlusIcon />}
                onClick={newPassConfigModal.onOpen}
              >
                New Pass Configuration
              </BusinessMenu>
            </Box>
          </HStack>
          <PassConfigurationTable
            passConfigurations={passConfigurationList}
            isLoading={isLoading}
            debouncedQuery={debouncedQuery}
            onClickEditPassBuilder={onClickEditPassBuilder}
            onClickCopyPassConfiguration={onClickCopyPassConfiguration}
            onClickClonePassConfiguration={onClickClonePassConfiguration}
            onClickViewUpdateHistory={onClickViewUpdateHistory}
          />
        </VStack>
      </PageContent>
      <NewDigitalMembershipCardPassConfigModal
        {...newPassConfigModal}
        refresh={refresh}
      />
      <CopyPassConfigurationDrawer
        isOpen={copyPassConfigurationDrawer.isOpen}
        onClose={onCloseCopyPassConfigurationDrawer}
        businessId={business.BusinessId as string}
        passConfiguration={selectedPassConfiguration}
      />
      <ClonePassConfigurationDrawer
        isOpen={clonePassConfigurationDrawer.isOpen}
        onClose={onCloseClonePassConfigurationDrawer}
        businessId={business.BusinessId as string}
        passConfiguration={selectedPassConfiguration}
        onCloned={onClonedPassConfiguration}
      />
      <PassConfigurationUpdateHistoryDrawer
        isOpen={updateHistoryDrawer.isOpen}
        onClose={onCloseUpdateHistoryDrawer}
        businessId={business.BusinessId as string}
        passConfiguration={selectedPassConfiguration}
      />
    </>
  );
};
